import { PDFDocument } from "pdf-lib";

const LETTERHEAD_URL = "/ATIAM-Letter-head-2026.pdf";

/**
 * Stamps the school letterhead as the background of the first page of a
 * jsPDF-generated document. Subsequent pages are left as plain content.
 *
 * @param contentBytes - ArrayBuffer returned by `jsPDF.output('arraybuffer')`
 * @returns Uint8Array of the final merged PDF
 */
export async function stampLetterheadOnFirstPage(
  contentBytes: ArrayBuffer,
): Promise<Uint8Array> {
  const [letterheadBytes] = await Promise.all([
    fetch(LETTERHEAD_URL).then((r) => r.arrayBuffer()),
  ]);

  const [letterheadDoc, contentDoc] = await Promise.all([
    PDFDocument.load(letterheadBytes),
    PDFDocument.load(contentBytes),
  ]);

  const outputDoc = await PDFDocument.create();

  // Embed the first (and only expected) page of the letterhead
  const [lhEmbedded] = await outputDoc.embedPdf(letterheadDoc, [0]);
  const lhPage = letterheadDoc.getPage(0);
  const { width, height } = lhPage.getSize();

  // Embed all content pages
  const contentCount = contentDoc.getPageCount();
  const allContentEmbedded = await outputDoc.embedPdf(
    contentDoc,
    Array.from({ length: contentCount }, (_, i) => i),
  );

  for (let i = 0; i < contentCount; i++) {
    const page = outputDoc.addPage([width, height]);

    if (i === 0) {
      // Draw letterhead first so it sits behind the content
      page.drawPage(lhEmbedded, { x: 0, y: 0, width, height });
    }

    // Draw report content on top
    page.drawPage(allContentEmbedded[i], { x: 0, y: 0, width, height });
  }

  return outputDoc.save();
}

/**
 * Triggers a browser download of the given PDF bytes.
 */
export function downloadPdfBytes(bytes: Uint8Array, filename: string): void {
  const blob = new Blob([bytes.buffer as ArrayBuffer], {
    type: "application/pdf",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
